import { Button, Group, Paper, Progress, Stack, Text } from '@mantine/core';
import { Workflow } from '@/app/workflows/actions/interfaces';
import ChevronIcon from '@/icons/chevron.svg';
import { Details } from '@/app/workflows/components/v6/organism/Details';
import { OperationList } from '@/app/workflows/components/v6/operations/OperationList';
import { useEditedWorkflowState } from '@/app/workflows/contexts/editedWorkflows';
import { EditedActionButtons } from '@/app/workflows/components/v6/atoms/EditedActionButtons';
import { EditableComponentWrapper } from '@/app/workflows/components/v6/molecues/EditableComponentWrapper';
import { EditName } from '@/app/workflows/components/v6/molecues/EditName';
import styles from './workflowListItem.module.css';
import { usePendingOutputsConsumer } from '@/app/assets/components/table/hooks/usePendingOutputs';
import { useRowSelection } from '@/app/assets/contexts/RowSelectionContext';
import { NameEditableColumn } from './NameEditableColumn';
import { OnboardingFlows } from '@/app/contexts/OnboardingContext/constants';
import { JsonForm } from '@/app/workflows/components/v7/FormWrapper';
import { DescriptionEditable } from './DescriptionEditable';
import { DoubleWorkflowContext } from '../../table/contexts/DoubleWorkflowContext';
import { isRpdx7 } from '@/app/workflows/utils/isRpdx7';

interface WorkflowListItemProps {
  workflow: Workflow;
  onRun: () => void;
  onWorkflowAdded: (workflowId: number) => void;
  opened?: boolean;
  toggle: (id: number) => void;
}

export function WorkflowListItem({ workflow, onRun, onWorkflowAdded, opened, toggle }: WorkflowListItemProps) {
  const { isWorkflowEdited } = useEditedWorkflowState();
  const isEdited = isWorkflowEdited(workflow.id);
  const { getSelectedRawAssetsIds } = useRowSelection();
  const { getPendingWorkflowProgress } = usePendingOutputsConsumer();

  const progress = getPendingWorkflowProgress(workflow.id, getSelectedRawAssetsIds());

  const isProgresAvailable = typeof progress === 'number';

  return (
    <DoubleWorkflowContext value={workflow}>
      <Paper p='md' className={isEdited ? styles.workflowEditedItem : styles.workflowItem}>
        <Group align='center' gap='xs'>
          <ChevronIcon
            {...{
              onClick: () => toggle(workflow.id),
              style: {
                cursor: 'pointer',
                transform: `rotate(${opened ? 180 : 0}deg)`,
                flexShrink: 0,
                color: 'var(--mantine-color-dark-9)',
              },
            }}
          />
          <Stack align='stretch' flex='1 1'>
            <Group justify='space-between'>
              <Group wrap='nowrap' flex='1'>
                <EditableComponentWrapper
                  displayComponent={(workflow) => (
                    <Group w='100%' h={50}>
                      <NameEditableColumn
                        workflow={workflow}
                        description={workflow.description}
                        getIsRowExpanded={() => !!opened}
                        name={workflow.name}
                      />
                      {isProgresAvailable && (
                        <Progress value={progress} color='teal.7' flex='1' miw='100px' maw='400px' />
                      )}
                    </Group>
                  )}
                  editComponent={({ setIsEdit, workflow }) => (
                    <EditName defaultName={workflow.name} setIsEdit={setIsEdit} workflow={workflow} />
                  )}
                  workflow={workflow}
                  boxProps={{ flex: '1' }}
                />
              </Group>
              {isEdited && <EditedActionButtons workflowId={workflow.id} onWorkflowAdded={onWorkflowAdded} />}
              {!isEdited && (
                <Button disabled={isProgresAvailable} onClick={onRun} id={`${OnboardingFlows.PRESET_MODAL}-1`}>
                  Run 3D Processor
                </Button>
              )}
            </Group>
          </Stack>
        </Group>
        {opened && (
          <Stack mt='sm' maw='100%'>
            {isRpdx7(workflow.rpdxVersion) ? (
              <>
                <DescriptionEditable workflow={workflow} />
                <JsonForm />
              </>
            ) : (
              <>
                <Details workflow={workflow} align='flex-start' ml='75px' wrappadable />
                <OperationList initialWorkflow={workflow} />
              </>
            )}
          </Stack>
        )}
      </Paper>
    </DoubleWorkflowContext>
  );
}
